/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import clsx from 'clsx';
import React, { FC, useState } from 'react';

import { CHART_COLORS } from '@futrli/colors';
import { formatCurrency, FormattedMessage, useIntl } from '@futrli/localisation';
import { PerformanceMetricsCashflowCashInOut } from '@futrli-sdk/predict';

import { UIBarChart } from '../../../../ui/charts/bar-chart/bar-chart';
import { UIDonutChart } from '../../../../ui/charts/donut-chart/donut-chart';

import parentStyles from '../performance.scss';
import styles from './cash-in-out.scss';

interface Props {
  type: 'in' | 'out';
  currency: string;
  data: PerformanceMetricsCashflowCashInOut;
}

export const CashInOut: FC<Props> = ({ type, currency, data }: Props) => {
  const [selectedChart, setSelectedChart] = useState(0);
  const intl = useIntl();

  const compData = {
    id: type === 'in' ? 'cash-in-from-invoices' : 'cash-out-from-bills'
  };

  const getFormattedRange = (min: number, max?: number) => {
    return max ? `${intl.formatNumber(min)} - ${intl.formatNumber(max)}` : `+${intl.formatNumber(min)}`;
  };

  const predictedInOutData = [
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(0, 7) }
      ),
      Amount: data.predicted_0_7_days
    },
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(8, 15) }
      ),
      Amount: data.predicted_8_15_days
    },
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(16, 30) }
      ),
      Amount: data.predicted_16_30_days
    },
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(31) }
      ),
      Amount: data.predicted_31_plus_days
    }
  ];

  const averageAgeData = [
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(0, 30) }
      ),
      Amount: data.raised_0_30_days
    },
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(31, 60) }
      ),
      Amount: data.raised_31_60_days
    },
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(61, 90) }
      ),
      Amount: data.raised_61_90_days
    },
    {
      label: intl.formatMessage(
        { id: 'predict.range_days', defaultMessage: '{range} Days' },
        { range: getFormattedRange(91) }
      ),
      Amount: data.raised_91_plus_days
    }
  ];

  const cashInLabels = [
    type === 'in'
      ? intl.formatMessage({ id: 'common.unpaid_invoices', defaultMessage: 'Unpaid Invoices' }).toUpperCase().split(' ')
      : intl.formatMessage({ id: 'common.unpaid_bills', defaultMessage: 'Unpaid Bills' }).toUpperCase().split(' '),
    type === 'in'
      ? intl
          .formatMessage({ id: 'predict.cash_flow.avg_monthly_revenue', defaultMessage: 'Avg monthly revenue' })
          .toUpperCase()
          .split(' ')
      : intl
          .formatMessage({
            id: 'predict.cash_flow.avg_monthly_expenditure',
            defaultMessage: 'Avg monthly expenditure'
          })
          .toUpperCase()
          .split(' ')
  ];
  const overdueLabels = [
    intl.formatMessage({ id: 'predict.overdue', defaultMessage: 'Overdue' }).toUpperCase(),
    intl.formatMessage({ id: 'predict.not_overdue', defaultMessage: 'Not Overdue' }).toUpperCase()
  ];

  return (
    <section data-testid={compData.id} className={clsx(parentStyles.card)}>
      <h3 className={clsx(parentStyles.cardTitle)}>
        {type === 'in' ? (
          <FormattedMessage id="predict.cash_in_from_invoices" defaultMessage="Cash in from invoices" />
        ) : (
          <FormattedMessage id="predict.cash_out_from_bills" defaultMessage="Cash out from bills" />
        )}
      </h3>
      <h4 className={clsx(parentStyles.cardDesc)}>
        <FormattedMessage id="predict.last_month_and_prior_11" defaultMessage="Last month and the prior 11" />
      </h4>
      <div className={clsx(styles.flexWrapper)}>
        <div className={clsx(styles.content)}>
          <p
            className={clsx(styles.selectable, selectedChart === 0 && styles.selected)}
            data-testid="select-chart-0"
            onClick={() => setSelectedChart(0)}
          >
            <span className={clsx(styles.value)}>{formatCurrency(+data.unpaid, intl, currency, 0, 0)}</span>
            <span className={clsx(styles.label)}>
              <FormattedMessage
                id="predict.unpaid_type_as_of_today"
                defaultMessage="Unpaid {type} as of today"
                values={{
                  type:
                    type === 'in'
                      ? intl.formatMessage({ id: 'predict.invoices', defaultMessage: 'Invoices' }).toLocaleLowerCase()
                      : intl.formatMessage({ id: 'predict.bills', defaultMessage: 'Bills' }).toLocaleLowerCase()
                }}
              />
            </span>
          </p>
          <p
            className={clsx(styles.selectable, selectedChart === 1 && styles.selected)}
            data-testid="select-chart-1"
            onClick={() => setSelectedChart(1)}
          >
            <span className={clsx(styles.value)}>{formatCurrency(+data.late, intl, currency, 0, 0)}</span>
            <span className={clsx(styles.label)}>
              <FormattedMessage
                id="predict.of_this_is_late"
                defaultMessage="Of this is late"
                tagName={React.Fragment}
              />
            </span>
          </p>
          <p
            className={clsx(styles.selectable, selectedChart === 2 && styles.selected)}
            data-testid="select-chart-2"
            onClick={() => setSelectedChart(2)}
          >
            <span className={clsx(styles.value)}>{formatCurrency(+data.predicted_0_7_days, intl, currency, 0, 0)}</span>
            <span className={clsx(styles.label)}>
              {type === 'in' ? (
                <FormattedMessage
                  id="predict.predicted_in_7_days"
                  defaultMessage="Predicted to come in (7 days)"
                  tagName={React.Fragment}
                />
              ) : (
                <FormattedMessage
                  id="predict.predicted_out_7_days"
                  defaultMessage="Predicted to go out (7 days)"
                  tagName={React.Fragment}
                />
              )}
            </span>
          </p>
          <p
            className={clsx(styles.selectable, selectedChart === 3 && styles.selected)}
            data-testid="select-chart-3"
            onClick={() => setSelectedChart(3)}
          >
            <span className={clsx(styles.value)}>
              <FormattedMessage id="predict.num_days" defaultMessage="{num} days" values={{ num: data.average_age }} />
            </span>
            <span className={clsx(styles.label)}>
              <FormattedMessage
                id="predict.average_age_of"
                defaultMessage="Average age of {type}"
                values={{
                  type:
                    type === 'in'
                      ? intl.formatMessage({ id: 'predict.invoices', defaultMessage: 'Invoices' }).toLocaleLowerCase()
                      : intl.formatMessage({ id: 'predict.bills', defaultMessage: 'Bills' }).toLocaleLowerCase()
                }}
              />
            </span>
          </p>
        </div>
        <div className={clsx(styles.chartSection)}>
          <div className={clsx(styles.chartWrapper)}>
            {selectedChart === 0 && (
              <UIBarChart
                testId="selected-chart-0"
                labels={cashInLabels}
                datasets={[
                  {
                    data: [+data.unpaid, +data.previous_3_month_average],
                    colors: [
                      CHART_COLORS[type === 'in' ? 'POSITIVE' : 'NEGATIVE'].PRIMARY,
                      CHART_COLORS[type === 'in' ? 'POSITIVE' : 'NEGATIVE'].SECONDARY
                    ],
                    barPercentage: 0.4
                  }
                ]}
              />
            )}
            {selectedChart === 1 && (
              <UIDonutChart
                testId="selected-chart-1"
                labels={overdueLabels}
                labelFormatter={(value: number) => formatCurrency(value, intl, currency, 0, 0)}
                datasets={[
                  {
                    data: [+data.late, +data.not_late],
                    colors: [
                      CHART_COLORS[type === 'in' ? 'POSITIVE' : 'NEGATIVE'].PRIMARY,
                      CHART_COLORS[type === 'in' ? 'POSITIVE' : 'NEGATIVE'].SECONDARY
                    ]
                  }
                ]}
                legend="bottom"
              />
            )}
            {selectedChart === 2 && (
              <UIBarChart
                testId="selected-chart-2"
                labels={predictedInOutData.map(e => e.label)}
                datasets={[
                  {
                    data: predictedInOutData.map(e => +e.Amount),
                    colors: CHART_COLORS[type === 'in' ? 'POSITIVE' : 'NEGATIVE'].PRIMARY
                  }
                ]}
              />
            )}
            {selectedChart === 3 && (
              <UIBarChart
                testId="selected-chart-3"
                labels={averageAgeData.map(e => e.label)}
                datasets={[
                  {
                    data: averageAgeData.map(e => +e.Amount),
                    colors: CHART_COLORS[type === 'in' ? 'POSITIVE' : 'NEGATIVE'].PRIMARY
                  }
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
